import type { FC, RefAttributes } from 'react';
import { Fragment, useCallback, useEffect, useRef, useState } from 'react';

import type { Message } from '../../../../../store/slices/chat/types';
import { throttle } from '../../../../../utils/throttle';
import { IconButton, Icons } from '../../../../controls/iconButton/IconButton';
import { MessageBubble } from '../messageBubble';

import styles from './MessagesWrapper.module.css';

interface MessagesWrapperProps extends RefAttributes<HTMLDivElement> {
  messages: Message[];
  currentUserId: string;
  onScrollToBottom: (isScrolledToBottom: boolean) => void;
}

export const MessagesWrapper: FC<MessagesWrapperProps> = ({ messages, currentUserId, onScrollToBottom }) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const wrapperRef = useRef<HTMLDivElement>(null);
  const [isScrolledToBottom, setIsScrolledToBottom] = useState(true);
  const [showScrollButton, setShowScrollButton] = useState(false);
  const [unreadMessageId, setUnreadMessageId] = useState<string | null>(null);

  const lastMessage = messages[messages.length - 1];
  const isLastMessageFromCurrentUser: boolean = lastMessage && lastMessage.userId === currentUserId;

  const scrollToBottom = useCallback((): void => {
    if (wrapperRef.current) {
      wrapperRef.current.scrollTop = wrapperRef.current.scrollHeight;
      setIsScrolledToBottom(true);
      setShowScrollButton(false);
      setUnreadMessageId(null);
    }
  }, []);
  const scrollToBottomThrottle = throttle(scrollToBottom, 500);

  // Automatically scroll to bottom on new message from current user
  useEffect(() => {
    if (isLastMessageFromCurrentUser) {
      scrollToBottom();
    }
  }, [isLastMessageFromCurrentUser, scrollToBottom, lastMessage]); // do not touch dependencies

  // Handle automatic scrolling for incoming messages when already at the bottom
  useEffect(() => {
    if (isScrolledToBottom && messages && !isLastMessageFromCurrentUser) {
      scrollToBottom();
    }
  }, [isLastMessageFromCurrentUser, isScrolledToBottom, messages, scrollToBottom]);

  // Preserve bottom scroll position on container resize (e.g. modal resize, drawer resize etc.)
  useEffect(() => {
    const containerElement = containerRef.current;
    if (!containerElement) return;

    const handleResize = (): void => {
      if (isScrolledToBottom) {
        requestAnimationFrame(scrollToBottomThrottle);
      }
    };

    const resizeObserver = new ResizeObserver(handleResize);
    resizeObserver.observe(containerElement);

    return () => {
      if (resizeObserver) {
        resizeObserver.disconnect();
      }
    };
  }, [isScrolledToBottom, scrollToBottom]); // Do not touch dependencies

  const handleScroll = useCallback(
    (event: Event): void => {
      const wrapperElement = event.target as HTMLDivElement;
      const scrollTop = wrapperElement.scrollTop;
      const clientHeight = wrapperElement.clientHeight;
      const scrollHeight = wrapperElement.scrollHeight;
      const isNearBottom = Math.abs(scrollTop + clientHeight - scrollHeight) <= 1;

      onScrollToBottom(isNearBottom);
      setIsScrolledToBottom(isNearBottom);
      setShowScrollButton(!isNearBottom);

      // Manage state when user scrolls up from bottom position
      if (!isNearBottom && !unreadMessageId && lastMessage) {
        setUnreadMessageId(lastMessage.messageId);
      }

      // Manage state when user approached bottom position
      if (isNearBottom) {
        setUnreadMessageId(null);
      }
    },
    [lastMessage, onScrollToBottom, unreadMessageId]
  );

  useEffect(() => {
    const wrapperElement = wrapperRef.current;
    if (!wrapperElement) return;

    wrapperElement.addEventListener('scroll', handleScroll);

    return () => wrapperElement.removeEventListener('scroll', handleScroll);
  }, [handleScroll]);

  return (
    <div className={styles.container} ref={containerRef}>
      <div className={styles.messagesWrapper} ref={wrapperRef}>
        {messages.map((message: Message) => (
          <Fragment key={message.messageId}>
            <div className={styles.messagesWrapper__message}>
              <MessageBubble {...message} currentUserId={currentUserId} />
            </div>
            {message.messageId === unreadMessageId && message.messageId !== messages[messages.length - 1].messageId && (
              <div className={styles.messagesWrapper__unreadMessage}>Unread Messages</div>
            )}
          </Fragment>
        ))}
      </div>

      {showScrollButton && (
        <div className={styles.messagesWrapper__scrollButton}>
          <IconButton icon={Icons.ChevronDown} large onClick={scrollToBottom} />
        </div>
      )}
    </div>
  );
};

MessagesWrapper.displayName = 'MessagesWrapper';
